import { t } from "@lingui/macro";
import { invariantResponse } from "@winamp/invariant";
import { redirectBackWithSuccess } from "@winamp/toast/index.js";
import { parseWithValibot } from "conform-to-valibot";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { $path } from "safe-routes";
import * as v from "valibot";
import { LABEL_READONLY_ROLES } from "~/models/role";
import { requireAuth } from "~/modules/auth/auth.server";
import { OpenAPI, getResponseErrors } from "~/modules/sdk/client.server";

export const getLabelCollaboratorRolesSchema = () =>
  v.object({
    roleIds: v.pipe(
      v.optional(v.union([v.string(), v.array(v.string())]), []),
      v.transform((roleIds) => (Array.isArray(roleIds) ? roleIds : [roleIds])),
      v.transform((roleIds) => roleIds.map(Number)),
    ),
  });

export async function action({ request, params }: ActionFunctionArgs) {
  invariantResponse(params.id, "Collaborator ID is required", {
    status: 404,
  });
  invariantResponse(params.labelId, "Label ID is required", {
    status: 404,
  });

  const { accessToken } = await requireAuth(request);

  const formData = await request.formData();

  const submission = parseWithValibot(formData, {
    schema: getLabelCollaboratorRolesSchema(),
  });

  // Send the submission back to the client if the status is not successful
  if (submission.status !== "success") {
    return submission.reply({
      resetForm: false,
      formErrors: [t`The permissions could not be applied.`],
    });
  }

  const { data, error } = await OpenAPI.PUT(
    "/admin/users/{userUid}/labels/{labelUid}/roles",
    {
      params: { path: { userUid: params.id, labelUid: params.labelId } },
      body: {
        roleIds: submission.value.roleIds,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (data) {
    return redirectBackWithSuccess(
      request,
      $path("/account/admin/collaborators/:id", { id: params.id }),
      submission.value.roleIds.length === 0
        ? {
            title: t`Collaborator access revoked!`,
            message: t`This collaborator has been revoked access to the given label.`,
          }
        : {
            title: t`Roles updated!`,
            message: t`The permissions have been updated successfully.`,
          },
    );
  }

  const { fieldErrors, formErrors } = getResponseErrors(error);

  return submission.reply({
    resetForm: false,
    fieldErrors,
    formErrors: fieldErrors
      ? formErrors
      : (formErrors ?? [t`An unexpected error occured.`]),
  });
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  invariantResponse(params.id, "User ID is required", {
    status: 404,
  });
  invariantResponse(params.labelId, "Label ID is required", {
    status: 404,
  });

  const { accessToken } = await requireAuth(request);

  const [{ data: user }, { data: label }, { data: roles }] = await Promise.all([
    OpenAPI.GET("/admin/users/{userUid}", {
      params: { path: { userUid: params.id } },
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
    OpenAPI.GET("/admin/labels/{labelUid}", {
      params: { path: { labelUid: params.labelId } },
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
    OpenAPI.GET("/auth/roles", {
      headers: { Authorization: `Bearer ${accessToken}` },
    }),
  ]);

  return {
    user: user?.data,
    label: label?.data,
    roles: roles?.data.roles.filter(
      (role) => !LABEL_READONLY_ROLES.includes(role.id),
    ),
  };
}
